import React, { useState, useEffect, useCallback } from 'react';
import HyperLinkField from './fields/HyperLinkField';
import NumericIntegerField from './fields/NumericIntegerField';
import InputTextField from './fields/InputTextField';
import NumericDecimalField from './fields/NumericDecimalField';
import ComboBoxField from './fields/ComboBoxField';
import InputMultiTextField from './fields/InputMultiTextField';
import InputDateField from './fields/InputDateField';
import MultiSelectField from './fields/MultiSelectField';
import ImageField from './fields/ImageField';

// Types
interface FieldType {
  FieldTypeId: number;
}

interface CustomFieldData {
  CustomFieldId: string;
  FieldTypeId?: number;
  FieldType?: FieldType;
  Value?: any;
  SocketConnectionId?: string;
  [key: string]: any;
}

interface CustomFieldLayer {
  CustomFields: CustomFieldData[];
  [key: string]: any;
}

interface ApiConfig {
  [key: string]: any;
}

interface Group {
  [key: string]: any;
}

interface Conversation {
  [key: string]: any;
}

interface CustomFieldItemProps {
  disableAutoSave?: boolean;
  canEdit?: boolean;
  customField: CustomFieldData;
  EditorEnabled?: boolean;
  apiConfig?: ApiConfig;
  configValueId?: string;
  projectId?: string;
  UpdateCustomFields: (field: CustomFieldData) => void;
  groups?: Group[];
  onCustomFieldUpdated?: () => void;
  isConversationDetailsView?: boolean;
  SocketConnectionId?: string;
  onOpenEditHistory?: () => void;
  conversation?: Conversation;
  updateCustomFieldsHistory?: () => void;
  loadingBarStart?: () => void;
  loadingBarComplete?: () => void;
  filterDetailsAllPhases?: boolean;
  conversationIds?: string;
}

interface LayersDetailsProps {
  conversation: Conversation;
  disableAutoSave?: boolean;
  EditorEnabled?: boolean;
  canEdit?: boolean;
  customField: CustomFieldLayer;
  apiConfig?: ApiConfig;
  loadingBarComplete?: () => void;
  loadingBarStart?: () => void;
  configValueId?: string;
  projectId?: string;
  groups?: Group[];
  onCustomFieldUpdated?: () => void;
  isConversationDetailsView?: boolean;
  SocketConnectionId?: string;
  onOpenEditHistory?: () => void;
  updateCustomFieldsHistory?: () => void;
  filterDetailsAllPhases?: boolean;
  UpdateCustomFieldsByLayer?: (layer: CustomFieldLayer, field: CustomFieldData) => void;
  conversationIds?: string;
  /* When false, return the <li> fields as a Fragment instead of wrapping them
     in their own <ul> — lets a parent merge these into a shared list. */
  wrapInUl?: boolean;
}

// Field type to component mapping
const FIELD_TYPE_COMPONENTS: Record<number, React.ComponentType<any>> = {
  1: InputTextField,
  2: NumericIntegerField,
  3: NumericDecimalField,
  4: NumericIntegerField,
  5: InputMultiTextField,
  6: InputDateField,
  7: MultiSelectField as any,
  9: ComboBoxField,
  10: ImageField,
  11: HyperLinkField,
};

const CustomFieldItem: React.FC<CustomFieldItemProps> = ({
  disableAutoSave,
  canEdit,
  customField,
  EditorEnabled,
  apiConfig,
  configValueId,
  projectId,
  UpdateCustomFields,
  groups,
  onCustomFieldUpdated,
  isConversationDetailsView,
  SocketConnectionId,
  onOpenEditHistory,
  conversation,
  updateCustomFieldsHistory,
  loadingBarStart,
  loadingBarComplete,
  filterDetailsAllPhases,
  conversationIds,
}) =>
{
  const fieldType = customField.FieldTypeId ?? customField.FieldType?.FieldTypeId;

  // Filter fields without value if enabled
  if (filterDetailsAllPhases && customField.Value == null)
  {
    return null;
  }

  if (!fieldType)
  {
    return null;
  }

  const FieldComponent = FIELD_TYPE_COMPONENTS[fieldType];

  if (!FieldComponent)
  {
    return null;
  }

  const fieldProps = {
    disableAutoSave,
    canEdit,
    customField: {
      ...customField,
      SocketConnectionId,
    },
    apiConfig,
    EditorEnabled,
    UpdateCustomFields,
  };

  // Additional props based on field type
  const additionalProps: Record<number, object> = {
    2: { conversation, onOpenEditHistory, updateCustomFieldsHistory },
    3: {
      conversation,
      onOpenEditHistory,
      updateCustomFieldsHistory,
      loadingBarStart,
      loadingBarComplete,
    },
    4: { conversation, onOpenEditHistory, updateCustomFieldsHistory },
    6: { onCustomFieldUpdated, loadingBarStart, loadingBarComplete },
    7: { configValueId, projectId, groups, isConversationDetailsView, conversationIds },
    9: { isConversationDetailsView },
  };

  const mergedProps = {
    ...fieldProps,
    ...(additionalProps[fieldType] || {}),
  };

  return (
    <li>
      <FieldComponent {...mergedProps} />
    </li>
  );
};

const LayersDetails: React.FC<LayersDetailsProps> = ({
  disableAutoSave,
  EditorEnabled,
  canEdit,
  customField,
  apiConfig,
  loadingBarComplete,
  loadingBarStart,
  configValueId,
  projectId,
  groups,
  onCustomFieldUpdated,
  isConversationDetailsView,
  SocketConnectionId,
  onOpenEditHistory,
  conversation,
  updateCustomFieldsHistory,
  filterDetailsAllPhases,
  UpdateCustomFieldsByLayer,
  conversationIds,
  wrapInUl = true,
}) =>
{
  const [customFields, setCustomFields] = useState<CustomFieldLayer>(customField);

  useEffect(() =>
  {
    setCustomFields(customField);
  }, [customField]);

  const UpdateCustomFields = useCallback(
    (updatedCustomField: CustomFieldData) =>
    {
      setCustomFields(prevCustomFields =>
      {
        const updatedFields: CustomFieldLayer = {
          ...prevCustomFields,
          CustomFields: prevCustomFields.CustomFields.map(field =>
            field.CustomFieldId === updatedCustomField.CustomFieldId ? updatedCustomField : field,
          ),
        };

        // Call parent callback asynchronously
        setTimeout(() =>
        {
          if (UpdateCustomFieldsByLayer)
          {
            UpdateCustomFieldsByLayer(updatedFields, updatedCustomField);
          }
        }, 0);

        return updatedFields;
      });
    },
    [UpdateCustomFieldsByLayer],
  );

  const items = customFields.CustomFields.map(field => (
    <CustomFieldItem
      key={`li-${field.CustomFieldId}-${(field as any).binary ? (field as any).binary : '0'}`}
      conversation={conversation}
      disableAutoSave={disableAutoSave}
      canEdit={canEdit}
      customField={field}
      apiConfig={apiConfig}
      EditorEnabled={EditorEnabled}
      configValueId={configValueId}
      projectId={projectId}
      UpdateCustomFields={UpdateCustomFields}
      groups={groups}
      onCustomFieldUpdated={onCustomFieldUpdated}
      isConversationDetailsView={isConversationDetailsView}
      SocketConnectionId={SocketConnectionId}
      onOpenEditHistory={onOpenEditHistory}
      updateCustomFieldsHistory={updateCustomFieldsHistory}
      loadingBarStart={loadingBarStart}
      loadingBarComplete={loadingBarComplete}
      filterDetailsAllPhases={filterDetailsAllPhases}
      conversationIds={conversationIds}
    />
  ));

  if (!wrapInUl)
  {
    return <>{items}</>;
  }

  return <ul className="_DEL_inner">{items}</ul>;
};

export default LayersDetails;
